import { jsx, jsxs } from 'react/jsx-runtime';
import { isScrollable, canScrollTo } from '@flex-design-system/fx-utils/element';
import { useMergedCSS, styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import { forwardRef, useState, useCallback } from 'react';
import { useResizeObserver } from '@flex-design-system/fx-utils/hooks';
import { ScrollArea } from '../../../scroll-area/ScrollArea.mjs';
import { FOG_HEIGHT, StyledFog, PADDING_X } from '../../styled.mjs';

const ModalAside = forwardRef(
  ({ children, height, side, css: cssFromProps, onScroll, ...props }, ref) => {
    const css = useMergedCSS(cssFromProps, {
      height
    });
    return /* @__PURE__ */ jsx(AsideRoot, { ref, side, css, ...props, children });
  }
);
const ModalAsideScrollArea = forwardRef(
  ({
    children,
    height,
    footer,
    side,
    css: cssFromProps,
    onScroll,
    bottomFog,
    ...props
  }, ref) => {
    const [viewportElement, setViewportElement] = useState(null);
    const [needScrollFog, setNeedScrollFog] = useState(false);
    const composedViewportRef = useComposedRefs(
      ref,
      (node) => setViewportElement(node)
    );
    const scrollable = isScrollable(viewportElement);
    const isFogDisabled = bottomFog === false;
    const isFogDisplayed = !isFogDisabled && needScrollFog;
    const css = useMergedCSS(cssFromProps, {
      paddingBottom: scrollable ? FOG_HEIGHT : 64
    });
    const handleScroll = useCallback(() => {
      setNeedScrollFog(canScrollTo(viewportElement, "bottom"));
    }, [viewportElement]);
    useResizeObserver(viewportElement, handleScroll);
    return /* @__PURE__ */ jsx(StyledScrollAreaRoot, { children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
      /* @__PURE__ */ jsx(
        StyledScrollAreaViewPort,
        {
          css,
          ref: composedViewportRef,
          onScroll: composeEventHandlers(onScroll, handleScroll),
          ...props,
          children: /* @__PURE__ */ jsx(StyledScrollAreaChildrenRoot, { children })
        }
      ),
      isFogDisplayed ? bottomFog ?? /* @__PURE__ */ jsx(
        StyledFog,
        {
          side: "bottom",
          color: "#FFFFFF",
          css: { marginBottom: -1, marginLeft: 1 }
        }
      ) : null
    ] }) });
  }
);
const AsideRoot = styled("aside", {
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  variants: {
    side: {
      left: {
        boxShadow: "$modalLeftAsideDivider"
      },
      right: {
        boxShadow: "$modalRightAsideDivider"
      }
    }
  }
});
const StyledScrollAreaRoot = styled(Primitive.div, {
  width: "100%",
  flexGrow: 1,
  display: "flex",
  overflow: "hidden",
  /**
   * ScrollArea div 의 height를 100% 로 주기 위함
   * 상위 (Modal.Content) 의 height 를 따라가기 위함
   */
  "& > div": {
    height: "100%"
  }
});
const StyledScrollAreaViewPort = styled(ScrollArea.Viewport_Legacy, {
  paddingBottom: FOG_HEIGHT,
  display: "flex",
  flexDirection: "column",
  "> div[style]": {
    height: "unset",
    flexGrow: 1
  }
});
const StyledScrollAreaChildrenRoot = styled("div", {
  display: "flex",
  flexDirection: "column",
  height: "100%"
});
const StyledAsideFooter = styled("footer", {
  width: "100%",
  padding: `0 ${PADDING_X}px 44px`,
  flexShrink: 0
});

export { ModalAside, ModalAsideScrollArea, StyledAsideFooter, StyledScrollAreaChildrenRoot };
//# sourceMappingURL=ModalAside.mjs.map
